// ─────────────────────────────────────────────────────────────
// Aperçu de liens dans l'application (missions, partages, preuves).
//
// Problème de départ : un iframe brut vers facebook.com / instagram.com
// ne s'affiche jamais (ces réseaux renvoient X-Frame-Options: DENY).
// La solution fiable consiste à utiliser LEURS adresses d'intégration
// officielles (plugins / embed), prévues pour être affichées dans un
// iframe. Quand aucun embed officiel n'existe (profil Facebook, lien X
// ou blog quelconque), on n'affiche plus un cadre vide : on montre une
// carte d'aperçu lisible avec le bouton d'ouverture.
// ─────────────────────────────────────────────────────────────

export type EmbedKind = 'iframe' | 'image' | 'video' | 'audio' | 'pdf' | 'link';

export interface EmbedInfo {
  kind: EmbedKind;
  /** Adresse réellement affichable dans un iframe (null si non intégrable). */
  src: string | null;
  /** Libellé lisible de la plateforme / du domaine. */
  label: string;
  icon: string;
  /** Explication affichée quand l'intégration est impossible. */
  reason: string;
}

const IMAGE_RE = /\.(png|jpe?g|webp|gif|avif|svg)(\?|#|$)/i;
const VIDEO_RE = /\.(mp4|webm|mov|m4v)(\?|#|$)/i;
const AUDIO_RE = /\.(mp3|wav|ogg|m4a|aac)(\?|#|$)/i;
const PDF_RE = /\.pdf(\?|#|$)/i;

function safeUrl(raw: string): URL | null {
  const value = (raw || '').trim();
  if (!value) return null;
  try {
    return new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
  } catch {
    return null;
  }
}

/** Extrait l'identifiant d'un post Facebook quand il est présent. */
function facebookPostHref(url: URL): string | null {
  const path = url.pathname;
  // /<page>/posts/<id>, /permalink.php?story_fbid=..., /photo.php?fbid=...
  if (/\/(posts|videos|reel|permalink)\//.test(path) || url.pathname.endsWith('permalink.php') || url.pathname.endsWith('photo.php')) {
    return url.toString();
  }
  return null;
}

/**
 * Transforme un lien utilisateur en aperçu réellement affichable.
 * Renvoie toujours un objet : `kind: 'link'` signifie « pas d'intégration
 * possible, afficher une carte d'aperçu avec ouverture externe ».
 */
export function resolveEmbed(raw: string): EmbedInfo {
  const url = safeUrl(raw);
  if (!url) {
    return { kind: 'link', src: null, label: 'Lien', icon: 'ri-link', reason: 'Lien invalide.' };
  }

  const host = url.hostname.replace(/^www\./, '').toLowerCase();
  const full = url.toString();

  // Fichiers directs
  if (IMAGE_RE.test(full)) return { kind: 'image', src: full, label: 'Image', icon: 'ri-image-line', reason: '' };
  if (VIDEO_RE.test(full)) return { kind: 'video', src: full, label: 'Vidéo', icon: 'ri-video-line', reason: '' };
  if (AUDIO_RE.test(full)) return { kind: 'audio', src: full, label: 'Audio', icon: 'ri-mic-line', reason: '' };
  if (PDF_RE.test(full)) return { kind: 'pdf', src: full, label: 'PDF', icon: 'ri-file-pdf-line', reason: '' };

  // YouTube
  if (host.endsWith('youtube.com') || host === 'youtu.be') {
    let videoId = '';
    if (host === 'youtu.be') videoId = url.pathname.split('/').filter(Boolean)[0] || '';
    else if (url.pathname.startsWith('/shorts/')) videoId = url.pathname.split('/')[2] || '';
    else if (url.pathname.startsWith('/embed/')) videoId = url.pathname.split('/')[2] || '';
    else videoId = url.searchParams.get('v') || '';
    if (videoId) {
      return {
        kind: 'iframe',
        src: `https://www.youtube.com/embed/${videoId}`,
        label: 'YouTube',
        icon: 'ri-youtube-line',
        reason: '',
      };
    }
    return { kind: 'link', src: null, label: 'YouTube', icon: 'ri-youtube-line', reason: 'Chaîne YouTube : ouverture dans un nouvel onglet.' };
  }

  // TikTok
  if (host.endsWith('tiktok.com')) {
    const match = url.pathname.match(/\/video\/(\d+)/);
    if (match) {
      return {
        kind: 'iframe',
        src: `https://www.tiktok.com/embed/v2/${match[1]}`,
        label: 'TikTok',
        icon: 'ri-tiktok-line',
        reason: '',
      };
    }
    return { kind: 'link', src: null, label: 'TikTok', icon: 'ri-tiktok-line', reason: 'Profil TikTok : ouverture dans un nouvel onglet.' };
  }

  // Instagram
  if (host.endsWith('instagram.com')) {
    const match = url.pathname.match(/\/(p|reel|reels|tv)\/([^/]+)/);
    if (match) {
      return {
        kind: 'iframe',
        src: `https://www.instagram.com/${match[1] === 'reels' ? 'reel' : match[1]}/${match[2]}/embed/captioned`,
        label: 'Instagram',
        icon: 'ri-instagram-line',
        reason: '',
      };
    }
    return { kind: 'link', src: null, label: 'Instagram', icon: 'ri-instagram-line', reason: 'Profil Instagram : ouverture dans un nouvel onglet.' };
  }

  // Facebook
  if (host.endsWith('facebook.com') || host === 'fb.com' || host === 'fb.watch') {
    const postHref = facebookPostHref(url);
    if (postHref) {
      return {
        kind: 'iframe',
        src: `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(postHref)}&show_text=true&width=500`,
        label: 'Facebook',
        icon: 'ri-facebook-circle-line',
        reason: '',
      };
    }
    return {
      kind: 'iframe',
      src: `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(full)}&tabs=timeline&width=500&height=400&small_header=false&hide_cover=false`,
      label: 'Facebook',
      icon: 'ri-facebook-circle-line',
      reason: 'Si le réseau refuse l’affichage, utilisez « Ouvrir » pour consulter la page.',
    };
  }

  // X / Twitter : pas d'embed sans script tiers
  if (host === 'x.com' || host.endsWith('twitter.com')) {
    return { kind: 'link', src: null, label: 'X (Twitter)', icon: 'ri-twitter-x-line', reason: 'X n’autorise pas l’affichage intégré : ouverture dans un nouvel onglet.' };
  }

  // Autres sites : beaucoup bloquent l'intégration → carte d'aperçu
  return {
    kind: 'link',
    src: null,
    label: host,
    icon: 'ri-global-line',
    reason: 'Ce site refuse l’affichage intégré. Ouvrez-le dans un nouvel onglet.',
  };
}

/** Vrai si le lien peut être affiché directement dans un cadre. */
export function isEmbeddable(raw: string): boolean {
  return resolveEmbed(raw).kind !== 'link';
}